import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const notifyAdminIfNewSignup = (u: User | null) => {
      if (!u) return;
      const createdAt = u.created_at ? new Date(u.created_at).getTime() : 0;
      const lastSignIn = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
      // First-ever sign-in: created and last_sign_in within 60s of each other.
      const isFirstSignIn = createdAt > 0 && Math.abs(lastSignIn - createdAt) < 60_000;
      if (!isFirstSignIn) return;
      const key = `signup-${u.id}`;
      // Client-side dedupe across page reloads.
      if (typeof window !== "undefined" && window.localStorage.getItem(key)) return;
      try { window.localStorage.setItem(key, "1"); } catch {}
      // 1. Welcome email to the new user.
      if (u.email) {
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "welcome",
            recipientEmail: u.email,
            idempotencyKey: `welcome-${u.id}`,
          },
        }).catch(() => {});
      }
      // 2. Admin notification copy.
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "admin-signup-notification",
          recipientEmail: "enrico@oplif.com",
          idempotencyKey: `admin-signup-${u.id}`,
          templateData: {
            userEmail: u.email,
            plan: "free",
            eventType: "signup",
            timestamp: new Date().toISOString(),
          },
        },
      }).catch(() => {});
    };

    // CRITICAL: register the listener BEFORE calling getSession() so we never
    // miss an INITIAL_SESSION / TOKEN_REFRESHED event fired during boot.
    // Only flip `loading` to false AFTER getSession() resolves — otherwise a
    // momentary null from an early callback can cause ProtectedRoute to
    // bounce the user to /auth (e.g. when returning from a Stripe redirect).
    let initialised = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (initialised) {
        // Subsequent auth changes (sign in/out/refresh) — safe to clear loading.
        setLoading(false);
      }
      if (event === "SIGNED_IN") {
        // Defer to avoid blocking the auth state callback.
        setTimeout(() => notifyAdminIfNewSignup(session?.user ?? null), 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      initialised = true;
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
